import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getJobQuestions, createJobQuestion, updateJobQuestion, deleteJobQuestion,
  reorderJobQuestions, copyJobQuestions, getJobs, getQuestionTemplates,
  type JobQuestion, type QuestionFieldType,
} from "../api";
import { useToast } from "../context/ToastContext";
import { useConfirm } from "../context/ConfirmContext";

export const FIELD_TYPE_LABELS: Record<QuestionFieldType, string> = {
  text: "Text", number: "Number", single_select: "Single Select",
  multi_select: "Multi Select", yes_no: "Yes / No",
};

type FormState = { label: string; field_type: QuestionFieldType; optionsText: string; required: boolean };
const BLANK_FORM: FormState = { label: "", field_type: "text", optionsText: "", required: false };

export function needsOptions(fieldType: QuestionFieldType) {
  return fieldType === "single_select" || fieldType === "multi_select";
}

export default function JobScreeningQuestions({ jobId }: { jobId: number }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const confirm = useConfirm();
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(BLANK_FORM);
  const [copySourceId, setCopySourceId] = useState("");
  const [templateId, setTemplateId] = useState("");

  const { data: questions = [], isLoading } = useQuery({
    queryKey: ["job-questions", jobId],
    queryFn: () => getJobQuestions(jobId),
  });
  const { data: allJobs = [] } = useQuery({ queryKey: ["jobs"], queryFn: getJobs });
  const otherJobs = (allJobs as any[]).filter(j => j.id !== jobId);
  const { data: templateData } = useQuery({ queryKey: ["question-templates"], queryFn: getQuestionTemplates });
  const templates = templateData?.templates ?? [];

  function invalidate() { qc.invalidateQueries({ queryKey: ["job-questions", jobId] }); }
  function resetForm() { setForm(BLANK_FORM); setAdding(false); setEditingId(null); }

  function toPayload(f: FormState) {
    return {
      label: f.label.trim(),
      field_type: f.field_type,
      options: needsOptions(f.field_type) ? f.optionsText.split("\n").map(s => s.trim()).filter(Boolean) : [],
      required: f.required,
    };
  }

  const createMut = useMutation({
    mutationFn: () => createJobQuestion(jobId, toPayload(form)),
    onSuccess: () => { invalidate(); resetForm(); },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to add question.", "error"),
  });
  const updateMut = useMutation({
    mutationFn: (id: number) => updateJobQuestion(jobId, id, toPayload(form)),
    onSuccess: () => { invalidate(); resetForm(); },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to save question.", "error"),
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteJobQuestion(jobId, id),
    onSuccess: () => invalidate(),
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to remove question.", "error"),
  });
  const copyMut = useMutation({
    mutationFn: (sourceId: number) => copyJobQuestions(jobId, sourceId),
    onSuccess: (created) => {
      invalidate();
      toast(created.length ? `Copied ${created.length} question(s).` : "That job has no questions to copy.", "success");
      setCopySourceId("");
    },
    onError: (e: any) => { toast(e?.response?.data?.detail || "Failed to copy questions.", "error"); setCopySourceId(""); },
  });
  const applyTemplateMut = useMutation({
    // Independent copy, same as copy-from-job (spec application-question-templates AC4) —
    // this job's new question has no lasting link back to the template.
    mutationFn: (t: { label: string; field_type: QuestionFieldType; options: string[]; required: boolean }) =>
      createJobQuestion(jobId, t),
    onSuccess: () => { invalidate(); toast("Question added from template.", "success"); setTemplateId(""); },
    onError: (e: any) => { toast(e?.response?.data?.detail || "Failed to add question.", "error"); setTemplateId(""); },
  });

  function startEdit(q: JobQuestion) {
    setEditingId(q.id);
    setAdding(false);
    setForm({ label: q.label, field_type: q.field_type, optionsText: q.options.join("\n"), required: q.required });
  }

  async function handleDelete(q: JobQuestion) {
    const ok = await confirm(`Remove screening question "${q.label}"?`, { confirmLabel: "Remove", danger: true });
    if (ok) deleteMut.mutate(q.id);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.label.trim()) { toast("Question label is required.", "error"); return; }
    if (needsOptions(form.field_type) && form.optionsText.split("\n").map(s => s.trim()).filter(Boolean).length < 2) {
      toast("Select questions need at least 2 options (one per line).", "error");
      return;
    }
    if (editingId) updateMut.mutate(editingId);
    else createMut.mutate();
  }

  function move(index: number, dir: -1 | 1) {
    const target = index + dir;
    if (target < 0 || target >= questions.length) return;
    const next = [...questions];
    [next[index], next[target]] = [next[target], next[index]];
    qc.setQueryData(["job-questions", jobId], next);
    reorderJobQuestions(jobId, next.map((q: JobQuestion) => q.id)).then(invalidate);
  }

  const saving = createMut.isPending || updateMut.isPending;

  return (
    <div style={cardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 4 }}>
        <h3 style={{ ...sectionHead, margin: 0, borderBottom: "none", paddingBottom: 0 }}>Screening Questions</h3>
        {!adding && !editingId && (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {otherJobs.length > 0 && (
              <select
                value={copySourceId}
                onChange={e => { const v = e.target.value; setCopySourceId(v); if (v) copyMut.mutate(Number(v)); }}
                style={{ ...inputStyle, width: 200, marginBottom: 0, fontSize: 12 }}
              >
                <option value="">Copy from another job…</option>
                {otherJobs.map((j: any) => <option key={j.id} value={j.id}>{j.title}</option>)}
              </select>
            )}
            {templates.length > 0 && (
              <select
                value={templateId}
                onChange={e => {
                  const v = e.target.value;
                  setTemplateId(v);
                  const t = templates.find(t => t.id === v);
                  if (t) applyTemplateMut.mutate({ label: t.label, field_type: t.field_type, options: t.options, required: t.required });
                }}
                style={{ ...inputStyle, width: 200, marginBottom: 0, fontSize: 12 }}
              >
                <option value="">Add from template…</option>
                {templates.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            )}
            <button type="button" onClick={() => setAdding(true)} style={btnStyle("#1e3a8a", "white")}>+ Add Question</button>
          </div>
        )}
      </div>
      <p style={{ fontSize: 12, color: "#64748b", margin: "2px 0 14px" }}>
        Custom fields candidates answer on the public application form — filterable on the candidate list.
      </p>

      {isLoading && <div style={{ fontSize: 13, color: "#94a3b8" }}>Loading…</div>}
      {!isLoading && questions.length === 0 && !adding && (
        <div style={{ fontSize: 13, color: "#94a3b8" }}>No screening questions yet.</div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {questions.map((q: JobQuestion, i: number) => editingId === q.id ? (
          <QuestionForm key={q.id} form={form} setForm={setForm} onSubmit={handleSubmit} onCancel={resetForm} saving={saving} submitLabel="Save" />
        ) : (
          <div key={q.id} style={{ display: "flex", alignItems: "center", gap: 10, border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 12px" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} style={reorderBtnStyle}>▲</button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === questions.length - 1} style={reorderBtnStyle}>▼</button>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                {q.label} {q.required && <span style={{ color: "#dc2626" }} title="Required">*</span>}
              </div>
              <div style={{ fontSize: 11, color: "#64748b" }}>
                {FIELD_TYPE_LABELS[q.field_type]}{q.options.length > 0 ? ` — ${q.options.join(", ")}` : ""}
              </div>
            </div>
            <button type="button" onClick={() => startEdit(q)} style={{ ...btnStyle("#f1f5f9", "#334155"), fontSize: 12 }}>Edit</button>
            <button type="button" onClick={() => handleDelete(q)} style={{ ...btnStyle("#fef2f2", "#dc2626"), fontSize: 12 }}>Remove</button>
          </div>
        ))}
      </div>

      {adding && (
        <div style={{ marginTop: 12 }}>
          <QuestionForm form={form} setForm={setForm} onSubmit={handleSubmit} onCancel={resetForm} saving={saving} submitLabel="Add Question" />
        </div>
      )}
    </div>
  );
}

interface QuestionFormProps {
  form: FormState; setForm: React.Dispatch<React.SetStateAction<FormState>>;
  onSubmit: (e: React.FormEvent) => void; onCancel: () => void; saving: boolean; submitLabel: string;
}

const QuestionForm: React.FC<QuestionFormProps> = ({ form, setForm, onSubmit, onCancel, saving, submitLabel }) => {
  return (
    <form onSubmit={onSubmit} style={{ border: "1px dashed #94a3b8", borderRadius: 8, padding: 14, background: "#f8fafc" }}>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 10, marginBottom: 10 }}>
        <div>
          <label style={labelStyle}>Question Label</label>
          <input value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
            style={inputStyle} placeholder="e.g. Expected Salary" autoFocus />
        </div>
        <div>
          <label style={labelStyle}>Type</label>
          <select value={form.field_type} onChange={e => setForm(f => ({ ...f, field_type: e.target.value as QuestionFieldType }))} style={inputStyle}>
            {Object.entries(FIELD_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </div>
      {needsOptions(form.field_type) && (
        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>Options (one per line, min 2)</label>
          <textarea value={form.optionsText} onChange={e => setForm(f => ({ ...f, optionsText: e.target.value }))}
            rows={3} style={{ ...inputStyle, resize: "vertical" }} placeholder={"Basic\nConversational\nFluent\nNative"} />
        </div>
      )}
      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#374151", marginBottom: 12 }}>
        <input type="checkbox" checked={form.required} onChange={e => setForm(f => ({ ...f, required: e.target.checked }))} />
        Required to submit application
      </label>
      <div style={{ display: "flex", gap: 8 }}>
        <button type="submit" disabled={saving} style={{ ...btnStyle("#1e3a8a", "white"), opacity: saving ? 0.6 : 1 }}>
          {saving ? "Saving…" : submitLabel}
        </button>
        <button type="button" onClick={onCancel} style={btnStyle("#e2e8f0", "#334155")}>Cancel</button>
      </div>
    </form>
  );
};

const cardStyle: React.CSSProperties = {
  background: "white", border: "1px solid #e2e8f0", borderRadius: 10,
  padding: 24, boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
};
const sectionHead: React.CSSProperties = {
  color: "#1e3a8a", fontSize: 15, margin: "0 0 12px", borderBottom: "2px solid #00c9a7", paddingBottom: 6,
};
const labelStyle: React.CSSProperties = {
  display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4,
};
const inputStyle: React.CSSProperties = {
  width: "100%", padding: "7px 10px", borderRadius: 6, border: "1px solid #d1d5db",
  fontSize: 13, color: "#334155", outline: "none", boxSizing: "border-box",
};
function btnStyle(bg: string, color: string): React.CSSProperties {
  return { background: bg, color, border: "none", padding: "7px 14px", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13 };
}
const reorderBtnStyle: React.CSSProperties = {
  background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: 10, padding: 0, lineHeight: 1,
};
